import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, FileText } from 'lucide-react';
import { Input } from './ui/input';
import { expedientesAPI } from '../api';

/**
 * Componente de búsqueda de expedientes con autocompletado
 * Busca en la base de datos mientras el usuario escribe
 */
const ExpedienteSearch = ({ 
  value,  // { id, numero_expediente } o null
  onChange,  // (expediente) => void
  placeholder = "Buscar expediente por número o título...",
  disabled = false,
  className = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Actualizar el input cuando cambia el valor externo
  useEffect(() => {
    if (value && value.numero_expediente) {
      setInputValue(value.numero_expediente);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar expedientes con debounce
  const searchExpedientes = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await expedientesAPI.getAll({ search: query, limit: 30 });
      setResults(response.data?.data || response.data || []);
      setIsOpen(true);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Error buscando expedientes:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio en el input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Limpiar selección si se modifica el texto
    if (value && newValue !== value.numero_expediente) {
      onChange(null);
    }

    // Debounce la búsqueda
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchExpedientes(newValue);
    }, 300);
  };

  // Seleccionar un expediente
  const handleSelect = (expediente) => {
    setInputValue(expediente.numero_expediente);
    onChange({
      id: expediente.id,
      numero_expediente: expediente.numero_expediente,
      titulo: expediente.titulo
    });
    setIsOpen(false);
    setResults([]);
  };

  // Limpiar selección
  const handleClear = () => {
    setInputValue('');
    onChange(null);
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Manejar navegación con teclado
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.length >= 2 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
        )}
        {!loading && value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
          >
            <X className="w-3 h-3 text-gray-500" />
          </button>
        )}
      </div>

      {/* Dropdown de resultados */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((expediente, index) => (
            <button
              key={expediente.id}
              type="button"
              onClick={() => handleSelect(expediente)}
              className={`w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-gray-100 transition-colors ${
                highlightedIndex === index ? 'bg-blue-50' : ''
              } ${index > 0 ? 'border-t' : ''}`}
            >
              <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm text-gray-900 truncate">
                  {expediente.numero_expediente}
                </p>
                {expediente.titulo && (
                  <p className="text-xs text-gray-500 truncate">
                    {expediente.titulo}
                  </p>
                )}
                {expediente.cliente_nombre && (
                  <p className="text-xs text-gray-400 truncate">
                    Cliente: {expediente.cliente_nombre}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Mensaje sin resultados */}
      {isOpen && inputValue.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-sm text-gray-500">
          No se encontraron expedientes para &ldquo;{inputValue}&rdquo;
        </div>
      )}

      {/* Indicador de selección */}
      {value && value.numero_expediente && (
        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Seleccionado: {value.numero_expediente}
          {value.titulo && ` - ${value.titulo}`}
        </p>
      )}
    </div>
  );
};

export default ExpedienteSearch;
